import React from 'react';
import { Link } from 'react-router-dom';
import { calculatorCards } from '../data/index';

interface CalculatorCard {
    emoji: string;
    icon: string;
    title: string;
    description: string;
    ctaLabel: string;
    // When present, the calculator is LIVE: the card renders an enabled Link
    // to this SPA route and a "Popular" badge instead of "Coming soon".
    to?: string;
    // When true, the CTA opens the target route in a new browser tab/window.
    newTab?: boolean;
}

/**
 * "Free Wealth-Building Calculators" section rendered below the Core Services
 * grid. The calculator experiences themselves are not built yet, so the CTA
 * buttons are presented as eye-catching (but disabled) placeholders with a
 * "Coming soon" badge until the dedicated calculator pages ship.
 */
const FreeCalculators: React.FC = () => {
    return (
        <section id="free-calculators" aria-labelledby="calculators-heading" className="w-full scroll-mt-24">
            <div className="mb-10 sm:mb-12 text-center max-w-3xl mx-auto">
                <span className="inline-block px-3 py-1 text-[10px] sm:text-xs font-bold tracking-widest text-emerald-400 uppercase bg-emerald-400/10 rounded-full border border-emerald-400/20">
                    Free Tools
                </span>
                <h2
                    id="calculators-heading"
                    className="mt-4 text-2xl sm:text-3xl lg:text-4xl font-black tracking-tight text-slate-900 dark:text-white leading-tight"
                >
                    Free{' '}
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-300">
                        Wealth-Building Calculators
                    </span>
                </h2>
                <p className="mt-4 text-sm sm:text-base text-slate-600 dark:text-slate-300 leading-relaxed">
                    Discover your future wealth, retirement readiness, and optimal investment strategy in minutes.
                </p>
            </div>

            <ul role="list" className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-7 max-w-4xl mx-auto">
                {(calculatorCards as CalculatorCard[]).map((card) => (
                    <li key={card.title} className="group flex">
                        <article
                            className="relative flex h-full w-full flex-col rounded-3xl p-6 sm:p-8
                                       bg-white/80 dark:bg-slate-900/70
                                       border border-slate-200 dark:border-slate-800
                                       backdrop-blur-sm
                                       shadow-sm hover:shadow-2xl hover:shadow-emerald-500/10
                                       transition-all duration-300
                                       hover:-translate-y-1 hover:border-emerald-400/60"
                        >
                            <div className="flex items-center gap-3">
                                <span
                                    className="flex h-14 w-14 items-center justify-center rounded-2xl text-3xl
                                               bg-gradient-to-br from-green-500/20 to-emerald-300/10
                                               border border-emerald-400/20"
                                    aria-hidden="true"
                                >
                                    {card.emoji}
                                </span>
                                {card.to ? (
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-emerald-500 dark:text-emerald-400">
                                        <i className="bx bxs-hot text-xs" aria-hidden="true"></i>
                                        Popular
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/30 bg-amber-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-500 dark:text-amber-400">
                                        Coming soon
                                    </span>
                                )}
                            </div>

                            <h3 className="mt-5 text-xl sm:text-2xl font-black tracking-tight text-slate-900 dark:text-white leading-snug">
                                {card.title}
                            </h3>

                            <p className="mt-4 text-sm sm:text-[15px] text-slate-600 dark:text-slate-300 leading-relaxed">
                                {card.description}
                            </p>

                            <div className="mt-auto pt-7">
                                {card.to ? (
                                    <Link
                                        to={card.to}
                                        target={card.newTab ? '_blank' : undefined}
                                        rel={card.newTab ? 'noopener noreferrer' : undefined}
                                        className="group/cta inline-flex w-full items-center justify-center gap-2
                                                   rounded-xl px-6 py-3.5
                                                   bg-gradient-to-r from-green-500 to-emerald-400
                                                   text-white text-sm sm:text-base font-bold
                                                   shadow-lg shadow-emerald-500/20
                                                   hover:-translate-y-0.5 hover:shadow-xl hover:shadow-emerald-500/40
                                                   active:translate-y-0
                                                   transition-all duration-300
                                                   focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-slate-900"
                                    >
                                        {card.ctaLabel}
                                        <i className="bx bx-right-arrow-alt text-xl transition-transform duration-300 group-hover/cta:translate-x-1" aria-hidden="true"></i>
                                    </Link>
                                ) : (
                                    <button
                                        type="button"
                                        disabled
                                        aria-disabled="true"
                                        title="Coming soon"
                                        className="inline-flex w-full cursor-not-allowed items-center justify-center gap-2
                                                   rounded-xl px-6 py-3.5
                                                   bg-gradient-to-r from-green-500/60 to-emerald-400/60
                                                   text-white/90 text-sm sm:text-base font-bold
                                                   shadow-lg shadow-emerald-500/10
                                                   opacity-80
                                                   transition-all duration-300"
                                    >
                                        {card.ctaLabel}
                                        <i className="bx bx-right-arrow-alt text-xl" aria-hidden="true"></i>
                                    </button>
                                )}
                            </div>
                        </article>
                    </li>
                ))}
            </ul>
        </section>
    );
};

export default FreeCalculators;
